import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSEO, generateWebPageSchema, generateBreadcrumbSchema } from '../../utils/seo';

export default function ContactPage() {
  // SEO Configuration
  useSEO({
    title: 'İletişim - Haklı Haksız | Bize Ulaşın',
    description: 'haklıhaksız.net ile iletişime geçin. Sorularınız, önerileriniz veya geri bildirimleriniz için bize ulaşın. E-posta: info@haklihaksiz.net',
    keywords: 'iletişim, haklı haksız, destek, müşteri hizmetleri, geri bildirim, öneri',
    canonical: '/contact',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateWebPageSchema(
          'İletişim - haklıhaksız.net',
          'Sorularınız, önerileriniz veya geri bildirimleriniz için bize ulaşın',
          '/contact'
        ),
        generateBreadcrumbSchema([
          { name: 'Ana Sayfa', url: '/' },
          { name: 'İletişim', url: '/contact' }
        ]),
        {
          '@type': 'ContactPage',
          name: 'İletişim',
          description: 'haklıhaksız.net iletişim sayfası'
        }
      ]
    }
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    alert('Mesajınız başarıyla gönderildi! En kısa sürede size dönüş yapacağız.');
    setFormData({ name: '', email: '', subject: '', message: '' });
  };

  const contactInfo = [
    {
      icon: 'ri-mail-line',
      title: 'E-posta',
      content: 'info@haklihaksiz.net',
      link: 'mailto:info@haklihaksiz.net',
      color: 'bg-blue-500'
    },
    {
      icon: 'ri-customer-service-line',
      title: 'Destek',
      content: 'Pazartesi - Cuma, 09:00 - 18:00',
      color: 'bg-emerald-500'
    },
    {
      icon: 'ri-map-pin-line',
      title: 'Konum',
      content: 'İstanbul, Türkiye',
      color: 'bg-purple-500'
    }
  ];

  const socialLinks = [
    { icon: 'ri-instagram-line', name: 'Instagram', link: '#', color: 'hover:text-pink-500' },
    { icon: 'ri-twitter-x-line', name: 'Twitter', link: '#', color: 'hover:text-blue-400' },
    { icon: 'ri-facebook-line', name: 'Facebook', link: '#', color: 'hover:text-blue-600' },
    { icon: 'ri-linkedin-line', name: 'LinkedIn', link: '#', color: 'hover:text-blue-700' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 via-white to-amber-50">
      {/* Navigation */}
      <nav className="bg-white/95 backdrop-blur-md shadow-md">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img src="https://static.readdy.ai/image/1dcacff907dfbea56e1f04a7f4ffdd79/131f7f20085d959b4f40e787e1603cf3.png" alt="Haklı Haksız Logo" className="h-14 w-14 object-contain" />
              <span className="text-xl font-semibold text-gray-900">haklıhaksız.net</span>
            </Link>
            
            <div className="flex items-center gap-4">
              <Link to="/" className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors whitespace-nowrap">
                Ana Sayfa
              </Link>
              <Link to="/submit" className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-6 py-2.5 rounded-full text-sm font-semibold hover:shadow-lg transition-all duration-300 hover:scale-105 whitespace-nowrap">
                Konu Aç
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-6xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
            İletişim
          </h1>
          <p className="text-2xl text-gray-600 leading-relaxed">
            Sorularınız, önerileriniz veya geri bildirimleriniz için bize ulaşın
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-6">
            {contactInfo.map((info, index) => (
              <div key={index} className="bg-white rounded-3xl p-8 text-center hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                <div className={`${info.color} w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4`}>
                  <i className={`${info.icon} text-white text-3xl`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  {info.title}
                </h3>
                {info.link ? (
                  <a href={info.link} className="text-gray-600 hover:text-orange-500 transition-colors">
                    {info.content}
                  </a>
                ) : (
                  <p className="text-gray-600">{info.content}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-12 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                Bize Mesaj Gönderin
              </h2>
              <p className="text-lg text-gray-600">
                Formu doldurun, en kısa sürede size dönüş yapalım
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    Adınız Soyadınız <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Adınız ve soyadınız"
                    className="w-full px-6 py-4 border-2 border-gray-200 rounded-2xl focus:outline-none focus:border-orange-500 transition-colors text-base"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    E-posta Adresiniz <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="ornek@email.com"
                    className="w-full px-6 py-4 border-2 border-gray-200 rounded-2xl focus:outline-none focus:border-orange-500 transition-colors text-base"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Konu <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder="Mesajınızın konusu"
                  className="w-full px-6 py-4 border-2 border-gray-200 rounded-2xl focus:outline-none focus:border-orange-500 transition-colors text-base"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Mesajınız <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Mesajınızı buraya yazın..."
                  rows={6}
                  maxLength={500}
                  className="w-full px-6 py-4 border-2 border-gray-200 rounded-2xl focus:outline-none focus:border-orange-500 transition-colors text-base resize-none"
                  required
                />
                <p className="text-sm text-gray-500 mt-2">
                  {formData.message.length}/500 karakter
                </p>
              </div>

              <button
                type="submit"
                className="w-full bg-gradient-to-r from-orange-500 to-red-500 text-white px-8 py-5 rounded-full text-lg font-semibold hover:shadow-xl transition-all duration-300 hover:scale-105 whitespace-nowrap"
              >
                Mesajı Gönder
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Social Media */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">
            Sosyal Medyada Bizi Takip Edin
          </h2>
          <div className="flex justify-center gap-6">
            {socialLinks.map((social, index) => (
              <a
                key={index}
                href={social.link}
                className={`w-16 h-16 bg-white border-2 border-gray-200 rounded-2xl flex items-center justify-center text-gray-700 ${social.color} transition-all duration-300 hover:scale-110 hover:shadow-lg`}
                aria-label={social.name}
              >
                <i className={`${social.icon} text-2xl`}></i>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Teaser */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
            Sıkça Sorulan Sorular
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Aklınıza takılan sorular mı var? SSS sayfamızı ziyaret edin
          </p>
          <Link
            to="/faq"
            className="inline-block bg-gray-900 text-white px-8 py-4 rounded-full text-base font-semibold hover:bg-gray-800 transition-all duration-300 hover:scale-105 whitespace-nowrap"
          >
            SSS'ye Git
          </Link>
        </div>
      </section>
    </div>
  );
}
